import { Button, ButtonGroup, ButtonMenu, Css, Icon, Tag, useTestIds } from "@homebound/beam";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  addEntityParam,
  createDevelopmentProductOfferingUrl,
  createProductOfferingConfigsUrl,
  createProductOfferingUrl,
  createProjectProductOfferingUrl,
} from "src/RouteUrls";
import { ProgressPill } from "src/components";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { usePersistedToggle } from "src/hooks";
import { EmptyState } from "src/routes/components/EmptyState";
import {
  ProductOfferingsDetailFragment,
  useProductOfferingsPageQuery,
} from "src/routes/developments/product-offerings/ProductOfferingsPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { DevelopmentParams, ProjectParams } from "src/routes/routesDef";
import { isDefined, queryResult, readyPlanStatusToTagType } from "src/utils";
import { programDataBathRange, programDataRange } from "src/utils/programData";
import { EmptyPlanAndOptionImage } from "../plan-and-options/components/EmptyStateSvgs";
import { ProductOfferingsTable } from "./ProductOfferingsTable";
import { UpdatesAvailableButton } from "./components/UpdatesAvailable";

export function ProductOfferingsPage() {
  const { developmentId, projectId } = useParams<DevelopmentParams & ProjectParams>();
  const history = useHistory();
  const query = useProductOfferingsPageQuery({
    variables: {
      filter: {
        development: isDefined(developmentId) ? [developmentId] : undefined,
        project: isDefined(projectId) ? [projectId] : undefined,
      },
    },
  });

  return queryResult(query, {
    data: ({ productOfferings }) => (
      <>
        <PageHeader
          title="Product Offerings"
          right={
            <>
              {productOfferings.entities.length !== 0 && (
                <>
                  {(developmentId || projectId) && (
                    <ButtonMenu
                      trigger={{ label: "Actions" }}
                      items={[
                        {
                          label: "Compare Configurations",
                          onClick: createProductOfferingConfigsUrl(developmentId, projectId),
                        },
                      ]}
                    />
                  )}
                  <Button
                    label="Create New"
                    onClick={() =>
                      history.push(createProductOfferingUrl(addEntityParam, undefined, developmentId, projectId))
                    }
                  />
                </>
              )}
            </>
          }
        />
        <ProductOfferingsPageView
          developmentId={developmentId}
          productOfferings={productOfferings.entities}
          projectId={projectId}
        />
      </>
    ),
  });
}

type ProductOfferingsPageViewProps = {
  productOfferings: ProductOfferingsDetailFragment[];
  developmentId?: string;
  projectId?: string;
};

export function ProductOfferingsPageView({
  productOfferings,
  developmentId,
  projectId,
}: ProductOfferingsPageViewProps) {
  const { length } = productOfferings;
  const history = useHistory();
  const [onTileView, toggleView] = usePersistedToggle("productOfferingsTileView", true);
  return length === 0 ? (
    <EmptyState
      message={`There are no Product Offerings for this ${isDefined(developmentId) ? "development" : "project"} yet`}
      svg={<EmptyPlanAndOptionImage />}
      button={
        <Button
          label="Create Product Offering"
          onClick={() => history.push(createProductOfferingUrl(addEntityParam, undefined, developmentId, projectId))}
        />
      }
    />
  ) : (
    <>
      <div css={Css.df.gap1.aic.pb2.$}>
        <div css={Css.smSb.$}>View</div>
        <ButtonGroup
          size="md"
          buttons={[
            {
              icon: "tile",
              tooltip: "Tile View",
              active: onTileView,
              onClick: toggleView,
            },
            {
              icon: "list",
              tooltip: "List View",
              active: !onTileView,
              onClick: toggleView,
            },
          ]}
        />
      </div>
      {onTileView ? (
        <div data-testid="productOfferingList" css={Css.dg.gap3.pb2.jcc.gtc("repeat(auto-fill, 369px)").$}>
          {productOfferings.map((po) => (
            <ProductOfferingCard key={po.id} productOffering={po} />
          ))}
        </div>
      ) : (
        <div>
          <ProductOfferingsTable productOfferings={productOfferings} />
        </div>
      )}
    </>
  );
}

type ProductOfferingCardProp = {
  productOffering: ProductOfferingsDetailFragment;
};

export function ProductOfferingCard({ productOffering }: ProductOfferingCardProp) {
  const tid = useTestIds({}, "productOfferingCard");
  const { id, name, status, coverAsset, minProgramData, maxProgramData, options, planPackage, bidOutPercentage } =
    productOffering;
  const coverPhotoUrl = coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG;
  const elevationsCount = options?.filter((option) => option?.type.isElevation).length ?? 0;

  return (
    <Link
      {...tid}
      css={Css.bgWhite.wPx(369).br8.bshBasic.p3.df.fdc.jcsb.gap3.aifs.onHover.bshHover.$}
      to={
        productOffering.development
          ? createDevelopmentProductOfferingUrl(productOffering.development!.id, id, productOffering.aggregateLatest.id)
          : createProjectProductOfferingUrl(productOffering.project!.id, id, productOffering.aggregateLatest.id)
      }
    >
      <div css={Css.w100.color("black").$}>
        <div css={Css.relative.$}>
          <img src={coverPhotoUrl} alt="Product Offering cover photo" {...tid.coverPhoto} css={Css.hPx(184).ma.$} />
          <div css={Css.absolute.top1.left1.$}>
            <Tag text={status.name} type={readyPlanStatusToTagType[status.code]} {...tid.status} />
          </div>
        </div>
        <div css={Css.mt3.$}>
          <div css={Css.df.jcsb.aic.$}>
            <div css={Css.df.aic.gap1.$}>
              <h4 css={Css.xl.mbPx(4).$} {...tid.name}>
                {name}
              </h4>
              <span>{productOffering.aggregateLatest.displayVersion}</span>
              <UpdatesAvailableButton
                copyInProgress={productOffering.copyInProgress}
                productOfferingId={productOffering.id}
                updates={productOffering.availableUpdates}
                isArchived={status.code === ReadyPlanStatus.Archived}
                showText={false}
              />
            </div>
            <Icon icon="verticalDots" />
          </div>
          <div css={Css.df.fdc.aic.w100.mt1.mb2.$}>
            <div css={Css.df.w100.$}>
              <span css={Css.sm.w50.$}>{programDataRange(minProgramData, maxProgramData, "sellableSqft")} sq ft</span>
              <span css={Css.sm.w50.$}>{elevationsCount} elevations</span>
            </div>
            <div css={Css.df.w100.$}>
              <span css={Css.sm.w50.$}>{programDataRange(minProgramData, maxProgramData, "bedrooms")} bed</span>
              <span css={Css.sm.w50.$}>Width: {programDataRange(minProgramData, maxProgramData, "widthInFeet")}</span>
            </div>
            <div css={Css.df.w100.$}>
              <span css={Css.sm.w50.$}>{programDataBathRange(minProgramData, maxProgramData)} bath</span>
              <span css={Css.sm.w50.$}>Depth: {programDataRange(minProgramData, maxProgramData, "depthInFeet")}</span>
            </div>
          </div>
          <div css={Css.sm.mb1.$}>{planPackage.code}</div>
          <ProgressPill progress={bidOutPercentage} changeColorOnCompleted />
        </div>
      </div>
    </Link>
  );
}
